/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useEffect, memo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  CircularProgress,
  Theme,
  useTheme,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useTranslation } from 'react-i18next';

import { Product } from './types';
import { VpBorderRadius, vpBoxShadow } from './styles';
import AppTooltip from '../../components/varietyPlanner/AppTooltip';
import { TranslationKeys } from '../../../i18n/locales/defaultResources';
import {
  generateColumns,
  getAttributeValue,
} from '../../../helpers/varietyPlanner/tableHelpers';

const COLUMN_CONSTRAINTS = {
  name: { minWidth: 150, maxWidth: 250, defaultWidth: 200 },
  default: { minWidth: 150, maxWidth: 200, defaultWidth: 150 },
};

const makeStyles = (theme: Theme) => ({
  sectionContainer: {
    position: 'relative',
    height: '100%',
    width: '100%',
  },
  loadingContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    zIndex: 10,
    pointerEvents: 'none',
  },
  loadingPaper: {
    padding: 2,
    borderRadius: 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    pointerEvents: 'auto',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.2s ease-in-out',
    border: '1px solid rgba(0, 0, 0, 0.05)',
  },
  customTableContainer: {
    height: '100%',
    width: '100%',
    overflow: 'auto',
    boxShadow: vpBoxShadow,
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      borderRadius: VpBorderRadius.SMALL,
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
      },
    },
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
  },
  cellTable: {
    borderBottom: `1px solid ${theme.palette.tableDivider}`,
    padding: theme.spacing(1, 1.75),
  },
  stickyTableCell: {
    position: 'sticky',
    left: 0,
    zIndex: 3,
    backgroundColor: theme.palette.appWhite,
  },
  cellContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sortableColumnContainer: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 0.5,
  },
});

type SortDirection = 'asc' | 'desc' | null;

interface SortState {
  field: string;
  direction: SortDirection;
}

interface VpRecommendedProductsTableProps {
  rows: Product[];
  loading: boolean;
  isLoadingMore: boolean;
  error: boolean;
  hasMoreData: boolean;
  onLoadMore: () => void;
  sortConfig: SortState;
  onSort: (field: string) => void;
}

const getStatusStyling = (status?: string) => {
  switch (status) {
    case 'Recommended':
      return {
        color: 'vpTable.recommended',
        icon: (
          <ArrowUpwardIcon
            sx={{
              fontSize: '1rem',
              flexShrink: 0,
              color: (theme) => theme.palette.common.white,
            }}
          />
        ),
      };
    case 'NotRecommended':
      return {
        color: 'vpTable.notRecommended',
        icon: (
          <ArrowDownwardIcon
            sx={{
              fontSize: '1rem',
              flexShrink: 0,
              color: (theme) => theme.palette.common.white,
            }}
          />
        ),
      };
    case 'Neutral':
    default:
      return {
        color: 'vpTable.neutral',
        icon: (
          <ArrowForwardIcon
            sx={{
              fontSize: '1rem',
              color: (theme) => theme.palette.common.white,
            }}
          />
        ),
      };
  }
};

const VpRecommendedProductsTable: React.FC<
  Readonly<VpRecommendedProductsTableProps>
> = ({
  rows,
  loading,
  isLoadingMore,
  error,
  hasMoreData,
  onLoadMore,
  sortConfig,
  onSort,
}) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const initialFetchCompleted = useRef(false);

  const theme = useTheme();
  const styles = makeStyles(theme);

  //Forced to DE lng, not edtiable in translation modal as per business requirement on this component

  const { t } = useTranslation();
  const columns = generateColumns(rows, t);

  const rowVirtualizer = useVirtualizer({
    count: hasMoreData ? rows.length + 1 : rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 44,
    overscan: 2,
  });

  useEffect(() => {
    if (isLoadingMore || !hasMoreData) return;
    if (rowVirtualizer.getVirtualItems().length === 0) return;
    if (!initialFetchCompleted.current) {
      initialFetchCompleted.current = true;
      return;
    }

    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();
    if (!lastItem) return;

    if (
      lastItem &&
      lastItem.index >= rows.length - 1 &&
      hasMoreData &&
      !isLoadingMore
    ) {
      onLoadMore();
    }
  }, [
    hasMoreData,
    onLoadMore,
    rows.length,
    isLoadingMore,
    rowVirtualizer.scrollOffset,
    rowVirtualizer.getTotalSize(),
  ]);

  if (error) {
    return (
      <Box p={4}>
        {t(TranslationKeys.variety_planner_table_error_loading_data)}
      </Box>
    );
  }

  return (
    <Box sx={styles.sectionContainer}>
      {(loading || isLoadingMore) && (
        <Box sx={styles.loadingContainer}>
          <Paper elevation={3} sx={styles.loadingPaper}>
            <CircularProgress
              size={40}
              sx={{ color: theme.palette.questionsForm.formBlue }}
            />
            <Typography mt={1} variant="appButtonText">
              {t(TranslationKeys.variety_planner_table_loading_text)}
            </Typography>
          </Paper>
        </Box>
      )}
      <TableContainer
        component={Paper}
        ref={parentRef}
        sx={styles.customTableContainer}
      >
        <Table
          stickyHeader
          aria-label="sticky table"
          sx={{ tableLayout: 'auto', flexGrow: 1 }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                key="status-column"
                sx={{
                  minWidth: COLUMN_CONSTRAINTS.name.minWidth,
                  maxWidth: COLUMN_CONSTRAINTS.name.maxWidth,
                  width: COLUMN_CONSTRAINTS.name.defaultWidth,
                  ...styles.cellTable,
                  ...styles.stickyTableCell,
                }}
              ></TableCell>

              {columns
                .filter((col) => col.field !== 'CH_0')
                .map((column, index) => (
                  <TableCell
                    key={`${column.field}-${index}`}
                    align="left"
                    onClick={() => onSort(column.field)}
                    sx={{
                      minWidth: COLUMN_CONSTRAINTS.default.minWidth,
                      maxWidth: COLUMN_CONSTRAINTS.default.maxWidth,
                      width: COLUMN_CONSTRAINTS.default.defaultWidth,
                      cursor: column.sortable ? 'pointer' : 'default',
                      ...styles.cellTable,
                      userSelect: 'none',
                      padding: 2,
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      '&:hover': {
                        backgroundColor: (theme) =>
                          column.sortable
                            ? theme.palette.tableDivider
                            : theme.palette.appWhite,
                      },
                    }}
                  >
                    <Box sx={styles.cellContent}>
                      <AppTooltip
                        title={column.headerName || ''}
                        isInfoTooltip={false}
                      >
                        <Typography
                          variant="appH4"
                          color="questionsForm.formLabel"
                          component="p"
                        >
                          {column.headerName}
                        </Typography>
                      </AppTooltip>

                      <Box sx={styles.sortableColumnContainer}>
                        {sortConfig.field === column.field &&
                          sortConfig.direction &&
                          (sortConfig.direction === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 20 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 20 }} />
                          ))}
                      </Box>
                    </Box>
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && (
              <>
                <TableRow>
                  <TableCell
                    colSpan={columns.length + 1}
                    style={{
                      height: `${rowVirtualizer.getVirtualItems()[0]?.start || 0}px`,
                      padding: 0,
                      border: 0,
                    }}
                  />
                </TableRow>

                {rowVirtualizer.getVirtualItems().map((virtualRow: any) => {
                  const isLoaderRow = virtualRow.index > rows.length - 1;
                  const item = rows[virtualRow.index];

                  return (
                    <TableRow
                      key={virtualRow.index}
                      sx={{ height: `${virtualRow.size}px` }}
                    >
                      {isLoaderRow && hasMoreData ? (
                        <TableCell colSpan={columns.length + 1} align="center">
                          <Typography variant="body2" color="text.secondary">
                            {t(
                              TranslationKeys.variety_planner_table_loading_more_text
                            )}
                          </Typography>
                        </TableCell>
                      ) : (
                        <>
                          <TableCell
                            key={`${item?.productId}-product-name`}
                            sx={{
                              ...styles.cellTable,
                              ...styles.stickyTableCell,
                              zIndex: 1,
                            }}
                          >
                            <Box display="flex" alignItems="center">
                              <Box
                                sx={{
                                  backgroundColor: getStatusStyling(
                                    item.productStatus
                                  ).color,
                                  borderRadius: '50%',
                                  width: 22,
                                  height: 22,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mr: 1,
                                  flexShrink: 0,
                                }}
                              >
                                {getStatusStyling(item.productStatus).icon}
                              </Box>

                              <Typography
                                variant="appBodyMedium"
                                component="p"
                                ml={0.5}
                                fontWeight="bold"
                                sx={{
                                  color: getStatusStyling(item.productStatus)
                                    .color,
                                }}
                              >
                                {getAttributeValue(
                                  'CH_0',
                                  item.productAttributes
                                )}
                              </Typography>
                            </Box>
                          </TableCell>
                          {columns
                            .filter((col) => col.field !== 'CH_0')
                            .map((column) => {
                              const value = getAttributeValue(
                                column.field,
                                item.productAttributes
                              );

                              return (
                                <TableCell
                                  key={`${item?.productId}-${column.field}`}
                                  align="left"
                                  sx={{
                                    minWidth:
                                      COLUMN_CONSTRAINTS.default.minWidth,
                                    maxWidth:
                                      COLUMN_CONSTRAINTS.default.maxWidth,
                                    width:
                                      COLUMN_CONSTRAINTS.default.defaultWidth,
                                    ...styles.cellTable,
                                  }}
                                >
                                  <Typography
                                    variant="appBodyMedium"
                                    color="questionsForm.formLightGrey"
                                    component="p"
                                    noWrap
                                  >
                                    {value !== null && value !== undefined
                                      ? value
                                      : '-'}
                                  </Typography>
                                </TableCell>
                              );
                            })}
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default memo(VpRecommendedProductsTable);
